import Page from "../Page";

import classNames from 'classnames'
import css from './ActivityPage.module.scss'
import { useSelector } from "../../store";
import Badge from "../presence/Badge";
import { useGraphUser } from "../../hooks";
import { UserPresenceStoreState } from "../../store/reducers/presence";
import dayjs from 'dayjs'
import { getGraphUserId } from "../../store/sagas/presence";
import { isEmptyOrSpaces } from "../../utils/text";
import { Dropdown, Icon, IDropdownOption } from "@fluentui/react";
import { useEffect, useMemo, useState } from "react";
import { AvailabilityPresenceState } from "../../model";

interface Props {
  className?: string
}

type ViewMode = 'icon' | 'list' | 'details'
type SortMode = 'name' | 'availability-activity-name'
type SortDirection = 'asc' | 'desc'

const STORAGE_VIEW_MODE = 'activity.viewMode'
const STORAGE_SORT_MODE = 'activity.sortMode'
const STORAGE_SORT_DIRECTION = 'activity.sortDirection'

const sortOptions: IDropdownOption<SortMode>[] = [
  { key: 'name', text: 'Name', data: 'name' },
  { key: 'availability-activity-name', text: 'Availability/Activity/Name', data: 'availability-activity-name' }
]

const availabilityOrder: AvailabilityPresenceState[] = [
  AvailabilityPresenceState.available,
  AvailabilityPresenceState.busy,
  AvailabilityPresenceState.doNotDisturb,
  AvailabilityPresenceState.beRightBack,
  AvailabilityPresenceState.away,
  AvailabilityPresenceState.offline,
]

function isViewMode(value: string | null): value is ViewMode {
  return value === 'icon' || value === 'list' || value === 'details'
}

function isSortMode(value: string | null): value is SortMode {
  return value === 'name' || value === 'availability-activity-name'
}

function isSortDirection(value: string | null): value is SortDirection {
  return value === 'asc' || value === 'desc'
}

function getAvailabilityRank(value: AvailabilityPresenceState | string) {
  const index = availabilityOrder.indexOf(value as AvailabilityPresenceState)
  return index < 0 ? availabilityOrder.length : index
}

function getActivityRank(value: string) {
  return value.toLocaleLowerCase()
}

function normalizeSortString(value: string | undefined | null) {
  return (value ?? '').trim().toLocaleLowerCase()
}

function getDisplayNameSortKey(presence: UserPresenceStoreState, userDisplayName: string | undefined, id: string) {
  const fallback = presence.externalIds._id || id
  return normalizeSortString(userDisplayName || fallback)
}

interface UserListItem {
  id: string
  presence: UserPresenceStoreState
  userDisplayName: string | undefined
}

export default function ActivityPage({ className }: Props) {
  const userPresences = useSelector(s => s.presence.users)
  const graphUsers = useSelector(s => s.graph.users)
  const haveUsers = userPresences.size > 0
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === 'undefined') return 'icon'
    const saved = window.localStorage.getItem(STORAGE_VIEW_MODE)
    return isViewMode(saved) ? saved : 'icon'
  })
  const [sortMode, setSortMode] = useState<SortMode>(() => {
    if (typeof window === 'undefined') return 'name'
    const saved = window.localStorage.getItem(STORAGE_SORT_MODE)
    return isSortMode(saved) ? saved : 'name'
  })
  const [sortDirection, setSortDirection] = useState<SortDirection>(() => {
    if (typeof window === 'undefined') return 'asc'
    const saved = window.localStorage.getItem(STORAGE_SORT_DIRECTION)
    return isSortDirection(saved) ? saved : 'asc'
  })

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_VIEW_MODE, viewMode)
    }
  }, [viewMode])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_SORT_MODE, sortMode)
    }
  }, [sortMode])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_SORT_DIRECTION, sortDirection)
    }
  }, [sortDirection])

  function setSortFromHeader(nextSortMode: SortMode) {
    if (sortMode === nextSortMode) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
      return
    }

    setSortMode(nextSortMode)
    setSortDirection('asc')
  }

  const users = useMemo<UserListItem[]>(() => {
    const unsorted: UserListItem[] = []
    userPresences.forEach((presence, id) => {
      const graphUserId = getGraphUserId(presence.externalIds)
      const userDisplayName = graphUserId ? graphUsers.get(graphUserId)?.displayName : undefined
      unsorted.push({
        id,
        presence,
        userDisplayName,
      })
    })
    return unsorted
  }, [graphUsers, userPresences])

  const sortedUsers = useMemo(() => {
    const values = [...users]

    values.sort((left, right) => {
      const leftName = getDisplayNameSortKey(left.presence, left.userDisplayName, left.id)
      const rightName = getDisplayNameSortKey(right.presence, right.userDisplayName, right.id)
      const direction = sortDirection === 'asc' ? 1 : -1

      if (sortMode === 'name') {
        return leftName.localeCompare(rightName) * direction
      }

      const availabilityCompare = getAvailabilityRank(left.presence.availability).valueOf() - getAvailabilityRank(right.presence.availability).valueOf()
      if (availabilityCompare !== 0) return availabilityCompare * direction

      const activityCompare = getActivityRank(left.presence.activity).localeCompare(getActivityRank(right.presence.activity))
      if (activityCompare !== 0) return activityCompare * direction

      return leftName.localeCompare(rightName) * direction
    })

    return values
  }, [sortDirection, sortMode, users])

  const containerClassName = classNames(css.activePresence, {
    [css.iconView]: viewMode === 'icon',
    [css.listView]: viewMode === 'list',
    [css.detailsView]: viewMode === 'details',
  })
  
  return <Page className={classNames(css.ActivityPage, className)}>
    <h1>Activity</h1>
    <div className={css.toolbar}>
      <div className={css.viewToggle} role='group' aria-label='Select activity view mode'>
        <button type='button' className={classNames(css.viewButton, viewMode === 'icon' && css.activeViewButton)} onClick={() => setViewMode('icon')}>
          <Icon iconName='Tiles' />
          <span>Icon</span>
        </button>
        <button type='button' className={classNames(css.viewButton, viewMode === 'list' && css.activeViewButton)} onClick={() => setViewMode('list')}>
          <Icon iconName='BulletedList' />
          <span>List</span>
        </button>
        <button type='button' className={classNames(css.viewButton, viewMode === 'details' && css.activeViewButton)} onClick={() => setViewMode('details')}>
          <Icon iconName='AlignLeft' />
          <span>Details</span>
        </button>
      </div>
      <Dropdown
        className={css.sortDropdown}
        label='Sort by'
        selectedKey={sortMode}
        options={sortOptions}
        onChange={(_, option) => {
          if (option?.data && isSortMode(option.data)) {
            setSortMode(option.data)
            setSortDirection('asc')
          }
        }}
      />
    </div>
    <div className={containerClassName}>
      {!haveUsers && <span>No users currently connected</span>}
      {haveUsers && viewMode === 'details' && <div className={css.detailsHeader}>
        <span className={css.detailsIconHeader} />
        <button
          type='button'
          className={classNames(css.detailsHeaderButton, sortMode === 'name' && css.activeSortHeader)}
          onClick={() => setSortFromHeader('name')}
        >
          <span>Name</span>
          {sortMode === 'name' && <Icon iconName={sortDirection === 'asc' ? 'SortUp' : 'SortDown'} className={css.sortIcon} />}
        </button>
        <span>Job title</span>
        <button
          type='button'
          className={classNames(css.detailsHeaderButton, sortMode === 'availability-activity-name' && css.activeSortHeader)}
          onClick={() => setSortFromHeader('availability-activity-name')}
        >
          <span>Presence</span>
          {sortMode === 'availability-activity-name' && <Icon iconName={sortDirection === 'asc' ? 'SortUp' : 'SortDown'} className={css.sortIcon} />}
        </button>
        <span>Updated</span>
      </div>}
      {haveUsers && sortedUsers.map(user => <UserCard key={user.id} id={user.id} presence={user.presence} viewMode={viewMode} />)}
    </div>
  </Page>
}

interface UserCardProps {
  id: string
  presence: UserPresenceStoreState
  viewMode: ViewMode
}

function UserCard({ id, presence, viewMode }: UserCardProps) {
  const graphUserId = getGraphUserId(presence.externalIds)
  const user = useGraphUser(graphUserId)
  const displayName = user?.displayName || presence.externalIds._id || id
  const presenceText = `${presence.availability}/${presence.activity}${presence.isOutOfOffice ? '/OutOfOffice' : ''}`

  if (viewMode === 'icon') {
    return <div className={classNames(css.userCard, css.userCardIcon)}>
      <Badge key={id} className={css.photo} id={id} size='3rem'>{''}</Badge>
      <div className={css.details}>
        <span className={css.name}>{displayName}</span>
        <span>{presenceText}</span>
      </div>
    </div>
  }

  if (viewMode === 'list') {
    return <div className={classNames(css.userCard, css.userCardList)}>
      <Badge key={id} className={css.photo} id={id} size='2rem'>{''}</Badge>
      <span className={css.name}>{displayName}</span>
      <span>{presenceText}</span>
      <span>{presence.status || '-'}</span>
      <span>Updated @ {dayjs(presence.lastUpdate).format('llll')}</span>
    </div>
  }

  return <div className={classNames(css.userCard, css.userCardDetails)}>
    <Badge key={id} className={css.photo} id={id} size='2rem'>{''}</Badge>
    <span className={css.name}>{displayName}</span>
    <span>{user?.jobTitle || '-'}</span>
    <div className={css.detailsColumn}>
      <span>{presenceText}</span>
      {presence.isOutOfOffice && presence.outOfOfficeMessage && !isEmptyOrSpaces(presence.outOfOfficeMessage) && <span>{presence.outOfOfficeMessage}</span>}
      {presence.idle && !presence.idleSince && <span>Idle</span>}
      {presence.idle && presence.idleSince && <span>Idle since {dayjs(presence.idleSince).format('llll')}</span>}
      {presence.returnTime && <span>Returns @ {dayjs(presence.returnTime).format('llll')}</span>}
      {Object.keys(presence.customData).map(cid => <CustomData id={cid} value={presence.customData[cid]} key={id+cid} />)}
      {presence.status && <span>{presence.status}</span>}
      {Object.keys(presence.externalIds).filter(eid => eid !== '_id').map(eid => <ExternalId id={eid} value={presence.externalIds[eid]} key={id+eid} />)}
    </div>
    <span>Updated @ {dayjs(presence.lastUpdate).format('llll')}</span>
  </div>
}

interface ExternalIdProps {
  id: string
  value: string
}

function ExternalId({id, value}: ExternalIdProps) {
  return <div className={css.externalId}>{id}: {value}</div>
}

interface CustomDataProps {
  id: string
  value: string
}

function CustomData({id, value}: CustomDataProps) {
  return <div className={css.customData}>{id}: {value}</div>
}